"use client"

import { useMutation, useQueryClient } from "@tanstack/react-query"
import {
  createAiUseCaseRoleAction,
  updateAiUseCaseRoleAction,
  deleteAiUseCaseRoleAction,
  composeAgentRolesAction,
  type AiUseCaseRoleView,
} from "@/server-actions/ai-use-case-roles"
import type { ComposedRoleBundle } from "@/lib/ai/roles/compose"
import type { AgentRoleScopeType } from "@/lib/ai/roles/types"
import { aiUseCaseRolesQueryKey } from "@/hooks/queries/ai-use-case-roles"
import { withQueryHandling } from "@/lib/with-query-handling"

function useInvalidateRoles() {
  const queryClient = useQueryClient()
  return async () => {
    await queryClient.invalidateQueries({ queryKey: aiUseCaseRolesQueryKey })
    // Agent-facing role selects cache per use case under a different key.
    await queryClient.invalidateQueries({ queryKey: ["agent-roles"] })
  }
}

export function useCreateAiUseCaseRoleMutation() {
  const invalidate = useInvalidateRoles()

  return useMutation({
    mutationFn: async (input: {
      use_case: string
      slug?: string
      name: string
      description?: string | null
      system_prompt: string
      settings?: Record<string, unknown>
    }): Promise<AiUseCaseRoleView> => {
      const result = await withQueryHandling(
        createAiUseCaseRoleAction({ ...input, settings: input.settings ?? {} }),
        { loading: "Creating role...", success: "Role created" },
      )
      if ("status" in result) throw new Error(result.errorMessage)
      return result
    },
    onSuccess: invalidate,
  })
}

export function useUpdateAiUseCaseRoleMutation() {
  const invalidate = useInvalidateRoles()

  return useMutation({
    mutationFn: async (input: {
      id: string
      name: string
      description?: string | null
      system_prompt: string
      settings?: Record<string, unknown>
    }): Promise<AiUseCaseRoleView> => {
      const result = await withQueryHandling(
        updateAiUseCaseRoleAction({ ...input, settings: input.settings ?? {} }),
        { loading: "Saving role...", success: "Role saved" },
      )
      if ("status" in result) throw new Error(result.errorMessage)
      return result
    },
    onSuccess: invalidate,
  })
}

export function useDeleteAiUseCaseRoleMutation() {
  const invalidate = useInvalidateRoles()

  return useMutation({
    mutationFn: async (id: string) => {
      const result = await withQueryHandling(deleteAiUseCaseRoleAction({ id }), {
        loading: "Deleting role...",
        success: "Role deleted",
      })
      if ("status" in result) throw new Error(result.errorMessage)
      return result
    },
    onSuccess: invalidate,
  })
}

/**
 * Compose the merged prompts for a role set (preview dialog). The caller must
 * keep the returned bundle and pass it to the agent create/update request as
 * `composed` — nothing is cached, so re-composing would yield different text.
 * Not invalidating anything: composing reads presets, it does not change them.
 */
export function useComposeAgentRolesMutation() {
  return useMutation({
    mutationFn: async (input: {
      scope_type: AgentRoleScopeType
      role_slugs: string[]
    }): Promise<ComposedRoleBundle> => {
      const result = await withQueryHandling(composeAgentRolesAction(input), {
        loading: "Composing the role prompt...",
      })
      if ("status" in result) throw new Error(result.errorMessage)
      return result.composed
    },
  })
}
